import React, {
    useCallback,
    useMemo,
    useState,
    useRef,
    useEffect,
} from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { Bluelagoon } from "@bluelagoon";
import axios from "axios";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@shadcn-in-nx/ui/dialog";

// Function to get API key - this will be implemented later
const getGoogleMapsApiKey = async ({ integrationId, apiKey }) => {
    const response = await axios(
        `${Bluelagoon.apiUrl}/proxy/integrations/api-key?${
            integrationId ? `integrationId=${integrationId}` : ""
        }`,
        {
            method: "GET",
            headers: {
                "x-api-key": apiKey,
            },
        }
    );

    return response.data.apiKey;
};

// Initialize global Google Maps loading state on window object
if (typeof window !== "undefined") {
    if (!window.__googleMapsLoadingState) {
        window.__googleMapsLoadingState = {
            isLoading: false,
            isLoaded: false,
            error: null,
            promise: null,
        };
    }
}

// Custom hook to manage Google Maps API loading globally
const useGoogleMapsApiLoader = (googleMapsApiKey, libraries) => {
    const [localState, setLocalState] = useState({
        isLoaded: false,
        loadError: null,
    });

    useEffect(() => {
        if (!googleMapsApiKey) return;

        // Check if API is already loaded globally
        if (window.__googleMapsLoadingState.isLoaded) {
            setLocalState({
                isLoaded: true,
                loadError: null,
            });
            return;
        }

        // Check if there's an ongoing loading process
        if (
            window.__googleMapsLoadingState.isLoading &&
            window.__googleMapsLoadingState.promise
        ) {
            window.__googleMapsLoadingState.promise
                .then(() => {
                    setLocalState({
                        isLoaded: true,
                        loadError: null,
                    });
                })
                .catch((error) => {
                    setLocalState({
                        isLoaded: false,
                        loadError: error,
                    });
                });
            return;
        }

        // Start new loading process
        window.__googleMapsLoadingState.isLoading = true;
        window.__googleMapsLoadingState.error = null;

        // Create a promise that resolves when the API is loaded
        const loadPromise = new Promise((resolve, reject) => {
            // Use the original useJsApiLoader logic here
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=${libraries.join(
                ","
            )}`;
            script.async = true;
            script.defer = true;

            script.onload = () => {
                window.__googleMapsLoadingState.isLoaded = true;
                window.__googleMapsLoadingState.isLoading = false;
                window.__googleMapsLoadingState.promise = null;
                resolve();
            };

            script.onerror = () => {
                const error = new Error("Failed to load Google Maps API");
                window.__googleMapsLoadingState.error = error;
                window.__googleMapsLoadingState.isLoading = false;
                window.__googleMapsLoadingState.promise = null;
                reject(error);
            };

            document.head.appendChild(script);
        });

        window.__googleMapsLoadingState.promise = loadPromise;

        loadPromise
            .then(() => {
                setLocalState({
                    isLoaded: true,
                    loadError: null,
                });
            })
            .catch((error) => {
                setLocalState({
                    isLoaded: false,
                    loadError: error,
                });
            });
    }, [googleMapsApiKey, libraries]);

    return localState;
};

// Inner Map component that handles the actual Google Maps rendering
const GoogleMapsComponent = ({
    center = { lat: 37.7749, lng: -122.4194 },
    zoom = 12,
    height = 400,
    width = "100%",
    markers = [],
    onMarkerClick = null,
    onMapClick = null,
    mapOptions = {},
    markerOptions = {},
    infoWindowOptions = {},
    className = "",
    showDefaultMarker = true,
    defaultMarkerPosition = null,
    mapContainerStyle = {},
    libraries = ["places"],
    loadingElement = <div>Loading...</div>,
    errorElement = <div>Error loading map</div>,
    googleMapsApiKey,
    searchValue = "",
    onLocationChange = null,
}) => {
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [map, setMap] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const searchTimeoutRef = useRef(null);

    // Use custom hook that manages global loading state
    const { isLoaded, loadError } = useGoogleMapsApiLoader(
        googleMapsApiKey,
        libraries
    );

    // Memoize map container style
    const containerStyle = useMemo(
        () => ({
            width,
            height,
            ...mapContainerStyle,
        }),
        [width, height, mapContainerStyle]
    );

    // Memoize center coordinates
    const mapCenter = useMemo(() => center, [center.lat, center.lng]);

    // Handle map load
    const onLoad = useCallback((map) => {
        setMap(map);
    }, []);

    // Handle map unmount
    const onUnmount = useCallback(() => {
        setMap(null);
    }, []);

    // Handle marker click
    const handleMarkerClick = useCallback(
        (marker, index) => {
            if (onMarkerClick) {
                onMarkerClick(marker, index);
            }
            setSelectedMarker(marker);
        },
        [onMarkerClick]
    );

    // Handle map click
    const handleMapClick = useCallback(
        (event) => {
            if (onMapClick) {
                onMapClick(event);
            }
            setSelectedMarker(null);
        },
        [onMapClick]
    );

    // Handle info window close
    const handleInfoWindowClose = useCallback(() => {
        setSelectedMarker(null);
    }, []);

    // Search for locations using Google Places API
    const searchLocations = useCallback(async (query) => {
        if (!query.trim() || !window.google || !window.google.maps) {
            return null;
        }

        setIsSearching(true);
        setSearchError(null);
        try {
            const service = new window.google.maps.places.AutocompleteService();
            const results = await new Promise((resolve, reject) => {
                service.getPlacePredictions(
                    {
                        input: query,
                        types: ["geocode", "establishment"],
                    },
                    (predictions, status) => {
                        if (
                            status ===
                            window.google.maps.places.PlacesServiceStatus.OK
                        ) {
                            resolve(predictions);
                        } else {
                            reject(new Error(`Places API error: ${status}`));
                        }
                    }
                );
            });

            // Return the first result if available
            return results && results.length > 0 ? results[0] : null;
        } catch (error) {
            console.error("Error searching locations:", error);
            setSearchError(error.message || "Failed to search for locations");
            return null;
        } finally {
            setIsSearching(false);
        }
    }, []);

    // Update location when searchValue changes
    useEffect(() => {
        if (
            !searchValue.trim() ||
            !map ||
            !window.google ||
            !window.google.maps
        ) {
            return;
        }

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Debounce search requests
        searchTimeoutRef.current = setTimeout(async () => {
            const result = await searchLocations(searchValue);
            if (result) {
                await handleLocationSelect(result.place_id);
            }
        }, 500);
    }, [searchValue, map, searchLocations]);

    // Handle location selection from search results
    const handleLocationSelect = useCallback(
        async (placeId) => {
            if (!window.google || !window.google.maps) return;

            try {
                setSearchError(null);
                const service = new window.google.maps.places.PlacesService(
                    map
                );
                const place = await new Promise((resolve, reject) => {
                    service.getDetails(
                        {
                            placeId: placeId,
                            fields: ["geometry", "name", "formatted_address"],
                        },
                        (place, status) => {
                            if (
                                status ===
                                window.google.maps.places.PlacesServiceStatus.OK
                            ) {
                                resolve(place);
                            } else {
                                console.log(status);
                                reject(
                                    new Error(`Places API error: ${status}`)
                                );
                            }
                        }
                    );
                });

                if (place && place.geometry && place.geometry.location) {
                    const newCenter = {
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng(),
                    };

                    // Update map center
                    if (map) {
                        map.panTo(newCenter);
                        map.setZoom(15);
                    }

                    // Call callback if provided
                    if (onLocationChange) {
                        onLocationChange({
                            center: newCenter,
                            name: place.name,
                            address: place.formatted_address,
                            placeId: placeId,
                        });
                    }
                }
            } catch (error) {
                console.error("Error getting place details:", error);
                setSearchError(
                    error.message || "Failed to get location details"
                );
            }
        },
        [map, onLocationChange]
    );

    // Default map options
    const defaultMapOptions = useMemo(
        () => ({
            zoomControl: true,
            streetViewControl: true,
            mapTypeControl: true,
            fullscreenControl: true,
            ...mapOptions,
        }),
        [mapOptions]
    );

    // Default marker options
    const defaultMarkerOptions = useMemo(
        () => ({
            clickable: true,
            draggable: false,
            ...markerOptions,
        }),
        [markerOptions]
    );

    // Default info window options
    const defaultInfoWindowOptions = useMemo(
        () => ({
            pixelOffset: { width: 0, height: -40 },
            ...infoWindowOptions,
        }),
        [infoWindowOptions]
    );

    // Show error if API fails to load
    if (loadError) {
        return errorElement;
    }

    // Show loading state while Google Maps API is loading
    if (!isLoaded) {
        return loadingElement;
    }

    return (
        <div className={`map-container ${className}`}>
            {/* Loading indicator for search */}
            {isSearching && (
                <div className="mb-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center text-blue-700">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                        Searching for location...
                    </div>
                </div>
            )}

            {/* Search error dialog */}
            {searchError && (
                <Dialog>
                    <DialogTrigger asChild>
                        <div className="mb-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg cursor-pointer hover:bg-red-100 transition-colors">
                            <div className="flex items-center text-red-700">
                                <svg
                                    className="w-4 h-4 mr-2"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                Search functionality not available
                            </div>
                        </div>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                Search Functionality Not Available
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <p className="text-gray-700">
                                The Google Maps API key provided doesn't have
                                Places search functionality enabled. This means
                                you won't be able to search for locations
                                directly on the map.
                            </p>
                            <p className="text-gray-700">
                                To enable search functionality, you need to:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                                <li>
                                    Enable the "Places API" in your Google Cloud
                                    Console
                                </li>
                                <li>
                                    Ensure your API key has access to Places
                                    services
                                </li>
                                <li>
                                    Check that billing is enabled for your
                                    Google Cloud project
                                </li>
                            </ul>
                            <p className="text-gray-700">
                                The map will still work for displaying locations
                                and markers, but search functionality will be
                                limited.
                            </p>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter}
                zoom={zoom}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onClick={handleMapClick}
                options={defaultMapOptions}
            >
                {/* Render custom markers */}
                {markers.map((marker, index) => (
                    <Marker
                        key={marker.id || index}
                        position={marker.position}
                        onClick={() => handleMarkerClick(marker, index)}
                        options={{
                            ...defaultMarkerOptions,
                            ...marker.options,
                        }}
                        label={marker.label}
                        title={marker.title}
                    />
                ))}

                {/* Show default marker if enabled */}
                {showDefaultMarker && (
                    <Marker
                        position={defaultMarkerPosition || center}
                        options={defaultMarkerOptions}
                    />
                )}

                {/* Info window for selected marker */}
                {selectedMarker && (
                    <InfoWindow
                        position={selectedMarker.position}
                        onCloseClick={handleInfoWindowClose}
                        options={defaultInfoWindowOptions}
                    >
                        <div className="info-window">
                            {selectedMarker.title && (
                                <h3 className="font-semibold text-gray-900 mb-2">
                                    {selectedMarker.title}
                                </h3>
                            )}
                            {selectedMarker.description && (
                                <p className="text-gray-700 text-sm">
                                    {selectedMarker.description}
                                </p>
                            )}
                            {selectedMarker.content && selectedMarker.content}
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>
        </div>
    );
};

// Main Map component that handles API key fetching
const GoogleMapComponent = ({
    center = { lat: 37.7749, lng: -122.4194 },
    zoom = 12,
    height = 400,
    width = "100%",
    markers = [],
    onMarkerClick = null,
    onMapClick = null,
    mapOptions = {},
    markerOptions = {},
    infoWindowOptions = {},
    className = "",
    showDefaultMarker = true,
    defaultMarkerPosition = null,
    mapContainerStyle = {},
    libraries = ["places"],
    loadingElement = <div>Loading...</div>,
    errorElement = <div>Error loading map</div>,
    integrationId,
    config,
    searchValue = "",
    onLocationChange = null,
}) => {
    const [googleMapsApiKey, setGoogleMapsApiKey] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const didRequestApiKey = useRef(false);

    // Get API key
    useEffect(() => {
        if (didRequestApiKey.current) return;

        if (!integrationId || !config?.apiKey) {
            setError("Missing integration ID or API key configuration");
            setIsLoading(false);
            return;
        }

        didRequestApiKey.current = true;
        setIsLoading(true);
        setError(null);

        const fetchApiKey = async () => {
            try {
                const apiKey = await getGoogleMapsApiKey({
                    integrationId: integrationId,
                    apiKey: config?.apiKey,
                });
                setGoogleMapsApiKey(apiKey);
                setIsLoading(false);
            } catch (error) {
                console.error("Failed to fetch Google Maps API key:", error);
                setError("Failed to load Google Maps API key");
                setIsLoading(false);
            }
        };

        fetchApiKey();
    }, [integrationId, config]);

    // Show loading state while fetching API key
    if (isLoading) {
        return loadingElement;
    }

    // Show error if API key fetch failed
    if (error || !googleMapsApiKey) {
        return errorElement;
    }

    // Render the actual map component with the fetched API key
    return (
        <GoogleMapsComponent
            center={center}
            zoom={zoom}
            height={height}
            width={width}
            markers={markers}
            onMarkerClick={onMarkerClick}
            onMapClick={onMapClick}
            mapOptions={mapOptions}
            markerOptions={markerOptions}
            infoWindowOptions={infoWindowOptions}
            className={className}
            showDefaultMarker={showDefaultMarker}
            defaultMarkerPosition={defaultMarkerPosition}
            mapContainerStyle={mapContainerStyle}
            libraries={libraries}
            loadingElement={loadingElement}
            errorElement={errorElement}
            googleMapsApiKey={googleMapsApiKey}
            searchValue={searchValue}
            onLocationChange={onLocationChange}
        />
    );
};

export default GoogleMapComponent;
